import { toDropdownOptions } from '@/helpers'
import type { IContainerDashboardFilters } from '@/types/rc/filters'
import type { IContainerCreationForm, IContainerStatusUpdate, IContainerUpdateForm } from '@/types/rc/forms'
import {
  addBulkContainers,
  createContainer,
  deleteContainer,
  getContainerDetails,
  getContainers,
  getContainerStatuses,
  updateContainer,
  updateContainerStatus,
} from '@/services/rc.service'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { ComputedRef, Reactive, Ref } from 'vue'
import { assignContainersToShipment, unassignContainerFromShipment } from '@/services/rc/shipments.service'
import { NotifyErrorWrapper, NotifyFail, NotifySuccess } from '@/helpers/core/notifications'

export const useContainers = () => {
  const client = useQueryClient()

  const getContainersQuery = (filters: Reactive<IContainerDashboardFilters>) =>
    useQuery({
      queryKey: ['containers', filters],
      queryFn: () => getContainers(filters),
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    })

  const containerStatusesOptionsQuery = useQuery({
    queryKey: ['container-statuses'],
    queryFn: () => getContainerStatuses().then(toDropdownOptions),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  const containerDetailsQuery = (
    containerId: ComputedRef<number | undefined> | Ref<number | undefined>,
    active: ComputedRef<boolean>,
    refetchOnWindowsFocus?: ComputedRef<boolean>
  ) =>
    useQuery({
      queryKey: ['container', containerId?.value],
      queryFn: () => getContainerDetails(containerId.value!),
      enabled: () => active.value && !!containerId.value,
      refetchOnWindowFocus: refetchOnWindowsFocus ?? false,
    })

  const createContainerMutation = useMutation({
    mutationFn: (container: IContainerCreationForm) => createContainer(container),
    onError: NotifyErrorWrapper('Failed to create container'),
    onSuccess: (data, container) => {
      client.invalidateQueries({ queryKey: ['containers'] })
      client.invalidateQueries({ queryKey: ['work-order-logistics', container.work_order?.number] })
      NotifySuccess(`Container created.`)
    },
  })

  const updateContainerMutation = useMutation({
    mutationFn: ({ id, container }: { id: number; container: IContainerUpdateForm }) => updateContainer(container, id),
    onError: NotifyErrorWrapper('Failed to update container'),
    onSuccess: (data, variables) => {
      client.invalidateQueries({ queryKey: ['containers'] })
      client.invalidateQueries({ queryKey: ['container', variables.id] })
      client.invalidateQueries({ queryKey: ['container-miners', variables.id] })
      NotifySuccess(`Container updated.`)
    },
  })

  const updateContainerStatusMutation = useMutation({
    mutationFn: (statusUpdate: IContainerStatusUpdate) => updateContainerStatus(statusUpdate, statusUpdate.id),
    onError: NotifyErrorWrapper('Failed to update status'),
    onSuccess: (data, container) => {
      client.invalidateQueries({ queryKey: ['containers'] })
      client.invalidateQueries({ queryKey: ['container', container.id] })
      NotifySuccess(`Container status updated.`)
    },
  })

  const deleteContainerMutation = useMutation({
    mutationFn: (id: number) => deleteContainer(id),
    onError: NotifyErrorWrapper('Failed to delete container'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['containers'] })
      client.invalidateQueries({ queryKey: ['work-order-logistics'] })
      client.invalidateQueries({ queryKey: ['shipment-details-containers'] })
      NotifySuccess(`Container deleted.`)
    },
  })

  const bulkAddContainersMutation = useMutation({
    mutationFn: addBulkContainers,
    onError: NotifyErrorWrapper('Failed to add containers'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['containers'] })
      client.invalidateQueries({ queryKey: ['work-order-logistics'] })
      client.invalidateQueries({ queryKey: ['shipment-details-containers'] })
      NotifySuccess(`Bulk containers added.`)
    },
  })

  const assignContainersToShipmentMutation = useMutation({
    mutationFn: ({ shipmentID, containerIDs }: { shipmentID: number; containerIDs: number[] }) =>
      assignContainersToShipment(shipmentID, containerIDs),
    onMutate: (variables) => {
      return { params: variables }
    },
    onSuccess: (_data, variables) => {
      NotifySuccess(`${variables.containerIDs.length} containers assigned successfully`)
      client.invalidateQueries({ queryKey: ['containers'] })
      client.invalidateQueries({ queryKey: ['work-order-logistics'] })
    },
    onError: (e) => {
      NotifyFail(e.message)
    },
  })

  const unassignContainerFromShipmentMutation = useMutation({
    mutationFn: ({ shipmentId, containerID }: { shipmentId: string; containerID: number }) =>
      unassignContainerFromShipment(shipmentId, containerID),
    onError: NotifyErrorWrapper('Failed to unassign container'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['containers'] })
      client.invalidateQueries({ queryKey: ['work-order-logistics'] })
      client.invalidateQueries({ queryKey: ['shipment-details-containers'] })
      NotifySuccess(`Container unassigned.`)
    },
  })

  return {
    getContainersQuery,
    containerDetailsQuery,
    containerStatusesOptionsQuery,
    createContainerMutation,
    updateContainerMutation,
    deleteContainerMutation,
    updateContainerStatusMutation,
    assignContainersToShipmentMutation,
    unassignContainerFromShipmentMutation,
    bulkAddContainersMutation,
  }
}
